<script setup lang="ts">
import { computed } from 'vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import Tag from '@/components/ui/InfoTag.vue';
import { StopType } from '@/@types/trip-updates';
import type { TripDetailRow } from './types';

const props = withDefaults(
  defineProps<{
    row: TripDetailRow;
    /** `passed` greys an already-served stop; `current` is the vehicle's next stop. */
    variant?: 'regular' | 'passed' | 'current';
    /** The timeline starts on this row: the bar gets its rounded cap. */
    first?: boolean;
    /** Terminus: the bar ends right below the dot. */
    last?: boolean;
    /** End of service: the times are hidden (the parent greys the bar). */
    ended?: boolean;
    /** This stop is the nearest to the passenger's position. */
    nearby?: boolean;
    /** "Prévu dans…" / "Passé il y a…", already translated by the parent. */
    subLabel?: string;
    /** A faded stub above the bar cap, continuing the passed segment. */
    fadedLead?: boolean;
    /** Realtime vehicle marker on this row's segment. */
    bus?: 'stopped' | 'transit' | null;
    /** The stop the passenger opened the trip from, served or not yet. */
    highlighted?: boolean;
  }>(),
  { variant: 'regular', subLabel: undefined, bus: null },
);

const isCanceled = computed<boolean>(() => props.row.type === StopType.CANCELED);

const mentionKey = computed<string | null>(() => {
  switch (props.row.type) {
    case StopType.CANCELED:
      return 'tags.unserved';
    case StopType.SHIFT:
      return 'stops.displaced';
    case StopType.AD_HOC:
    case StopType.REUSE:
      return 'stops.temporary';
    default:
      return null;
  }
});
</script>

<template>
  <div
    class="trip-stop-row"
    :class="[
      `trip-stop-row--${variant}`,
      {
        'trip-stop-row--first': first,
        'trip-stop-row--last': last,
        'trip-stop-row--lead': fadedLead,
        'trip-stop-row--canceled': isCanceled,
        'trip-stop-row--ended': ended,
        'trip-stop-row--highlighted': highlighted,
      },
    ]"
  >
    <div class="trip-stop-row__thermo">
      <div v-if="fadedLead" class="trip-stop-row__lead" />
      <div class="trip-stop-row__line" />
      <div class="trip-stop-row__dot" />
      <div
        v-if="bus"
        class="trip-stop-row__bus"
        :class="{ 'trip-stop-row__bus--stopped': bus === 'stopped' }"
      >
        <BaseIcon name="bus-front" />
      </div>
    </div>

    <div class="trip-stop-row__body">
      <div class="trip-stop-row__main">
        <div class="trip-stop-row__text">
          <span class="trip-stop-row__name" :class="{ 'trip-stop-row__name--canceled': isCanceled }">
            {{ row.name }}
          </span>
          <span v-if="subLabel" class="trip-stop-row__sub">{{ subLabel }}</span>
          <span v-if="mentionKey" class="trip-stop-row__mention">
            <BaseIcon name="circle-alert" class="trip-stop-row__mention-icon" />
            {{ $t(mentionKey) }}
          </span>
          <span v-if="nearby" class="trip-stop-row__nearby">
            <BaseIcon name="map-pin" class="trip-stop-row__nearby-icon" />
            {{ $t('trip.nearYou') }}
          </span>
          <div v-if="row.tags.length > 0" class="trip-stop-row__tags">
            <Tag v-for="tag in row.tags" :key="tag.tagType" :tag-type="tag.tagType" :tag-data="tag.tagData" />
          </div>
        </div>
        <span
          v-if="!ended"
          class="trip-stop-row__time"
          :class="{
            'trip-stop-row__time--canceled': isCanceled,
            'trip-stop-row__time--warning': row.timeWarning && !isCanceled,
          }"
        >
          {{ row.time }}
        </span>
      </div>
      <slot name="details" />
    </div>
  </div>
</template>

<style lang="scss">
.trip-stop-row {
  cursor: pointer;
  display: flex;
  width: 100%;

  &:active {
    background-color: $color-neutral-lower;
  }

  &--passed,
  &--canceled {
    background-color: $color-neutral-lower;
  }

  // Declared after the passed grey: an origin stop already served stays green
  &--highlighted {
    background-color: $color-primary-brand-light;
  }

  &__thermo {
    flex-shrink: 0;
    position: relative;
    width: 40px;
  }

  &__line {
    background-color: var(--timeline-color);
    bottom: 0;
    left: $spacing-4;
    position: absolute;
    top: 0;
    width: 16px;
  }

  // The already-travelled segment is washed out
  &--passed &__line {
    background-color: var(--timeline-color-faded);
  }

  // The bar starts here: rounded cap, dot right at the start
  &--first &__line {
    border-radius: $radius-pill $radius-pill 0 0;
    top: $spacing-4;
  }

  // The faded segment bleeds from the passed row; the solid bar caps below it
  &--lead &__line {
    border-radius: $radius-pill $radius-pill 0 0;
    top: 20px;
  }

  &__lead {
    background-color: var(--timeline-color-faded);
    height: 28px;
    left: $spacing-4;
    position: absolute;
    top: 0;
    width: 16px;
  }

  // Terminus: the bar stops right below the dot, rounded
  &--last &__line {
    border-radius: 0 0 $radius-pill $radius-pill;
    bottom: calc(100% - 38px);
  }

  &--last.trip-stop-row--first &__line {
    border-radius: $radius-pill;
  }

  &__dot {
    background-color: $color-white;
    border-radius: 50%;
    height: 12px;
    left: 18px;
    position: absolute;
    top: 24px;
    width: 12px;
  }

  &--first &__dot {
    top: 18px;
  }

  &--lead &__dot {
    top: 22px;
  }

  &__bus {
    align-items: center;
    background-color: $color-white;
    border: 1px solid $color-border-medium;
    border-radius: 50%;
    box-shadow: $shadow-v2;
    color: $color-neutral-highest;
    display: flex;
    font-size: 20px;
    height: 32px;
    justify-content: center;
    left: $spacing-2;
    position: absolute;
    // Approaching: the marker sits at the top of the row, over the stop's dot
    top: 4px;
    width: 32px;
    z-index: 2;

    &--stopped {
      top: 14px;
    }
  }

  &__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    padding: $spacing-4 $spacing-4 $spacing-4 $spacing-2;
  }

  &__main {
    align-items: flex-start;
    display: flex;
    gap: $spacing-2;
  }

  &__text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-1;
    min-width: 0;
  }

  &__name {
    color: $color-neutral-highest;
    font: $label-medium;
    letter-spacing: $letter-spacing-tight;
  }

  &--current &__name,
  &--highlighted &__name {
    font-weight: 600;
  }

  &__name--canceled {
    text-decoration: line-through;
  }

  // Fin de service: softer name, no time shown
  &--ended &__name {
    color: $color-text-dark;
  }

  &__sub {
    color: $color-text-neutral;
    font: $text-small;
    letter-spacing: $text-small-spacing;
  }

  // "Passé il y a…" reads bigger than "Prévu dans…" in the mockups
  &--passed &__sub {
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
  }

  &__mention {
    align-items: center;
    color: $color-warning;
    display: flex;
    font: $label-medium;
    gap: 6px;
    letter-spacing: $label-medium-spacing;
  }

  &__mention-icon {
    flex-shrink: 0;
    font-size: 16px;
  }

  &__nearby {
    align-items: center;
    align-self: flex-start;
    background-color: $color-neutral-low;
    border-radius: $radius-tag;
    color: $color-text-dark;
    display: inline-flex;
    font: 500 14px / 20px $font-poppins;
    gap: $spacing-1;
    letter-spacing: $letter-spacing-snug;
    padding: 2px 6px 2px $spacing-1;
  }

  &__nearby-icon {
    font-size: 14px;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-2;
    padding-top: $spacing-1;
  }

  &__time {
    color: $color-neutral-highest;
    flex-shrink: 0;
    font: $label-medium;
    letter-spacing: $letter-spacing-snug;
    white-space: nowrap;

    &--warning {
      color: $color-warning;
    }

    &--canceled {
      text-decoration: line-through;
    }
  }

  &--current &__time,
  &--highlighted &__time {
    font-weight: 600;
  }
}
</style>
